import {
    Component,
    Input,
    OnDestroy,
    OnChanges,
    OnInit,
    Injector,
} from '@angular/core';

import {
    notEmpty,
} from '@common/util';

import { DataType } from '../../../../data-type';

import {JobPharmaDetailService, MemberTaskInstanceForInputTabResponse} from '../../services';
import { VocabularyService } from '../../../../vocabularies/vocabulary.service';
import {
    Animal, Cohort,
    CohortMaterial,
    Entity,
    ExtendedJob,
    Placeholder,
    Sample,
    TaskCohortInput,
    TaskInput,
    TaskInstance,
    TaskPlaceholderInput
} from '@common/types';
import { JobPharmaTasksInputsTableService } from './job-pharma-tasks-inputs-table.service';
import { LoggingService } from '@services/logging.service';
import { formatDataAutomationId } from '@common/util/format-data-automation-id';
import {JobPharmaDataAccessService} from "../../services";
import {applyMixins, Base, CanUnsubscribeMixin, mixinUnsubscribe} from "@common/mixins";
import {CanLoadMixin, mixinLoadable} from "@common/mixins/loadable.mixin";
import {VisibleColumns} from "@common/util/table-columns/base-table-columns-helper";
import {TasksInputsTableColumnsHelper} from "./tasks-inputs-table-columns-helpers";

interface DataRow {
    task: TaskInstance;
    cohort?: Cohort;
    placeholder?: Placeholder;
    material?: Animal | Sample;
    inputs?: TaskPlaceholderInput[] | TaskInput[] | TaskCohortInput[];
    taskFirst?: boolean;

    childInputs?: Record<string, TaskInput[]>;
    classes?: Record<string, Record<string, boolean>>;
}

const ERROR_LOADING = "Error loading tasks inputs table";
const ERROR_LOADING_VOCABULARIES = "Error loading vocabularies";

const Mixins: CanUnsubscribeMixin & CanLoadMixin & typeof Base = applyMixins(mixinUnsubscribe, mixinLoadable);

@Component({
    selector: 'job-pharma-tasks-inputs-table',
    templateUrl: './job-pharma-tasks-inputs-table.component.html',
    providers: [JobPharmaTasksInputsTableService, TasksInputsTableColumnsHelper]
})
export class JobPharmaTasksInputsTableComponent extends Mixins implements OnChanges, OnDestroy, OnInit {
    readonly COMPONENT_LOG_TAG = 'job-pharma-tasks-inputs-table';
    readonly tabset = 'tasks';
    readonly tab = 'inputs';

    @Input() readonly: boolean;
    @Input() job: Entity<ExtendedJob>;

    @Input() isStudyDirector: boolean;
    taskPage = 1;

    // Non-member tasks
    tasks: TaskInstance[] = [];

    // Rows to show in the table
    rows: DataRow[] = [];

    // Visible columns
    visibleColumns: VisibleColumns;

    // Bulk Input form
    bulkInputTasks: any[] = [];
    bulkInputTask: any;
    bulkInputSelectedTaskAliases: string[] = [];
    bulkInputTaskAliases: string[] = [];
    bulkInputCohorts: any[] = [];
    bulkInputCohortKeys: number[] = [];
    bulkInputInputs: any[] = [];
    bulkInputInput: any = null;
    bulkInputValue: any = null;

    allLocked = false;

    loadingMessage = "Loading";

    constructor(
        injector: Injector,
        private jobPharmaDataAccessService: JobPharmaDataAccessService,
        private jobPharmaDetailService: JobPharmaDetailService,
        private vocabularyService: VocabularyService,
        private jobPharmaTasksInputsTableService: JobPharmaTasksInputsTableService,
        private loggingService: LoggingService,
        private columnsHelper: TasksInputsTableColumnsHelper,
    ) {
        super(injector);
    }

    async ngOnInit(): Promise<void> {
        super.ngOnInit();

        try {
            this.setLoading(true);

            this.initChangeDetection();
            this.initColumnSelect();

            await this.initialize();
        } catch (err) {
            this.loggingService.logError(ERROR_LOADING, err, this.COMPONENT_LOG_TAG);
        } finally {
            this.setLoading(false);
        }
    }

    ngOnChanges(changes: any): void {
        super.ngOnChanges(changes);

        if (changes.job && !changes.job.firstChange) {
            this.initJob();
        }
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    async initialize(): Promise<any> {
        await Promise.all([
            this.loadVocabularies(),
            this.initJob()
        ])
    }

    loadVocabularies(): Promise<any> {
        try {
            this.setLoading(true);

            return Promise.all([
                this.vocabularyService.ensureCVLoaded('cv_DataTypes'),
                this.vocabularyService.ensureCVLoaded('cv_DosingTables'),
            ]);
        } catch (err) {
            this.loggingService.logError(ERROR_LOADING_VOCABULARIES, err, this.COMPONENT_LOG_TAG, false);
        } finally {
            this.setLoading(false);
        }
    }

    allLockedChanged() {
        // locked or unlocked all the rows
        if (this.tasks) {
            for (const task of this.tasks) {
                task.IsLocked = this.allLocked;
            }
        }
    }

    isLockedChanged() {
        // Check if all the rows are locked
        this.allLocked = this.tasks.every((task) => task.IsLocked);
    }

    /**
     * Watch for external changes
     */
    initChangeDetection(): void {
        // Also watch for changes to Job.TaskJob
        this.subscribe(this.jobPharmaDetailService.jobTasksChanged$.subscribe(() => {
            this.initJob();
        }));

        this.subscribe(this.jobPharmaDetailService.tabRefresh$.subscribe((event) => {
            if (event.tabset === 'tasks' && event.tab === 'inputs') {
                this.initJob();
            }
        }));
    }

    /**
     * Initialize the column selections
     */
    initColumnSelect(): void {
        const {
            columnSelect,
            visibleColumns,
            updateVisibleColumns
        } = this.columnsHelper.create();
        this.visibleColumns = visibleColumns;

        // Register the columns
        this.subscribe(
            this.jobPharmaDetailService.registerColumnSelect(
                this.tabset,
                this.tab,
                columnSelect,
                updateVisibleColumns,
            )
        );

        updateVisibleColumns();
    }

    /**
     * Initialize the Job-related values.
     */
    async initJob(): Promise<void> {
        try {
            this.setLoading(true);
            // Load group task instances
            const loadTasks = this.jobPharmaDataAccessService.getGroupTaskInstancesForInputsTab(
                this.job.C_Job_key,
            );
            const loadCohorts = this.jobPharmaDataAccessService.getCohortsForInputsTab(
                this.job.C_Job_key,
            );

            const [tasks] = await Promise.all([loadTasks, loadCohorts])

            this.jobPharmaDetailService.sortTasksBySchedule(tasks);
            // Going to use these tasks...
            this.tasks = tasks;

            // ... but they need to be split into rows first to reduce the number of member task instances to load
            this.initRows();
            await this.loadMemberTaskInstancesWithExpands(this.taskPage);

            // init rows one more time after member tasks are loaded
            this.initRows();

            this.isLockedChanged();
            // Figure which tasks to show in the bulk input UI.
            this.initBulkInputTasks();
        } catch (error) {
            this.loggingService.logError(ERROR_LOADING, error, this.COMPONENT_LOG_TAG, true);
        } finally {
            this.setLoading(false);
        }
    }

    // function splits this.tasks array based on number of rows and page number, using 50 rows per page,
    // then load member task instances only for those task instances
     loadMemberTaskInstancesWithExpands(page: number): Promise<MemberTaskInstanceForInputTabResponse[]> {
        const startIndex = (page - 1) * 50;
        const endIndex = page * 50 - 1 > this.rows.length ? this.rows.length : page * 50;

        // creates new array with only the ids of the specified page
        const pageTaskInstances: number[] = this.rows.slice(startIndex, endIndex).map(ti => ti.task.C_TaskInstance_key);
        // extract unique task instance keys
        const uniqueTaskInstanceKeys = Array.from(new Set(pageTaskInstances));

        // load member task instances relevant to the page passed into the function
        return this.jobPharmaDataAccessService.getMemberTaskInstancesWitExpandsForInputsTab(
            uniqueTaskInstanceKeys
        );
    }

    initRows(): void {
        // These rows will appear in the table
        const rows: DataRow[] = [];

        for (const task of this.tasks) {
            // Each task may be split into multiple rows
            const taskRows: DataRow[] = [];

            if (task.TaskCohort) {
                this.addCohortInputsToTaskRow(taskRows, task);
            }

            if (task.TaskPlaceholder) {
                this.addPlaceholderInputsToTaskRow(taskRows, task);
            }

            if (task.TaskMaterial && task.MemberTaskInstance) {
               this.addMemberTaskInstanceInputsToTaskRow(taskRows, task);
            }

            if (taskRows.length > 0) {
                // Mark the first row of each task and add the task rows to the table
                taskRows.forEach((row: DataRow, index: number) => {
                    row.taskFirst = (index === 0);
                    row.classes = {
                        task: { 'task-extra': !row.taskFirst },
                    };

                    rows.push(row);
                });
            } else {
                // The task has no rows, so add a placeholder for it.
                rows.push({
                    task,
                    inputs: task.TaskInput,
                    taskFirst: true,
                    classes: {
                        task: {'task-extra': false},
                    }
                });

            }
        }

        // Use these rows
        this.rows = rows;
    }

    /**
     * Cohorts have their own inputs that are applied to all
     * their animals.
     *
     * @param taskRows Table rows
     * @param task Task Instance
     */
    addCohortInputsToTaskRow(taskRows: DataRow[], task: TaskInstance): void {
        for (const tc of task.TaskCohort) {
            const cohort = tc.Cohort;
            const inputs = tc.TaskCohortInput;

            // Prepare a lookup table for all the animals in the cohort.
            const animalMap: Record<number, boolean> = {};
            if (cohort.CohortMaterial) {
                cohort.CohortMaterial.forEach((cm: CohortMaterial) => {
                    if (cm.Material && cm.Material.Animal) {
                        animalMap[cm.C_Material_key] = true;
                    }
                });
            }

            // Prepare arrays of all the animal (child) inputs
            // keyed by Input key.
            const childInputs: DataRow["childInputs"] = {};
            if (inputs) {
                for (const input of inputs) {
                    childInputs[input.C_Input_key] = [];
                }
            }

            // Find all the member tasks for this cohort's animals
            // and add their inputs to the child inputs.
            if (task.MemberTaskInstance) {
                for (const ti of task.MemberTaskInstance) {
                    if (ti.TaskInput && ti.TaskMaterial) {
                        for (const tm of ti.TaskMaterial) {
                            if (animalMap[tm.C_Material_key]) {
                                for (const input of ti.TaskInput) {
                                    if (childInputs[input.C_Input_key]) {
                                        childInputs[input.C_Input_key].push(input);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Add a row to edit the cohort inputs
            taskRows.push({
                task,
                cohort,
                inputs,
                childInputs
            });
        }
    }

    /**
     * Placeholders have their own inputs
     *
     * @param taskRows Table rows
     * @param task Task Instance
     */
    addPlaceholderInputsToTaskRow(taskRows: DataRow[], task: TaskInstance): void {
        for (const tp of task.TaskPlaceholder) {
            // Add a row to edit the placeholder inputs
            if (this.jobPharmaDetailService.taskDontHaveMatchingCohort(tp, task.TaskCohort)) {
                taskRows.push({
                    task,
                    placeholder: tp.Placeholder,
                    inputs: tp.TaskPlaceholderInput
                });
            }
        }
    }

    /**
     * Individual materials added to the group task get their own rows
     *
     * @param taskRows Table rows
     * @param task Task Instance
     */
    addMemberTaskInstanceInputsToTaskRow(taskRows: DataRow[], task: TaskInstance): void {
        for (const tm of task.TaskMaterial) {
            if (tm.Material && (tm.Material.Animal || tm.Material.Sample)) {
                // Find the corresponding member task for the material
                const materialTask = task.MemberTaskInstance.find(ti => {
                    return ti.TaskMaterial && ti.TaskMaterial.some(
                        _tm => _tm.C_Material_key === tm.Material.C_Material_key
                    );
                });

                if (materialTask) {
                    // Add a row to edit the materials inputs
                    taskRows.push({
                        task,
                        material: tm.Material.Animal !== null ? tm.Material.Animal : tm.Material.Sample,
                        inputs: materialTask.TaskInput,
                    });
                }
            }
        }
    }

    /**
     * Handle a change to an input value.
     *
     * Update child input values when an input value is changed in the table.
     * For example, update the animal inputs values when a cohort input value
     * is changed.
     *
     * @param row The table row
     * @param input The change input
     */
    onInputValueChange(row: DataRow, input: any) {
        if (!row.childInputs || !row.childInputs[input.C_Input_key]) {
            // This row odes not have child inputs for this input
            return;
        }

        // Update all the child inputs
        const childInputs = row.childInputs[input.C_Input_key];
        for (const childInput of childInputs) {
            if (!childInput.TaskInstance.cv_TaskStatus || !childInput.TaskInstance.cv_TaskStatus.IsEndState) {
                childInput.InputValue = input.InputValue;
            }
        }
    }

    /**
     * Prepare the Task options in the Bulk Input fill down
     */
    initBulkInputTasks() {
        const tasks: any[] = [];
        const seen: any = {};

        // Find all the distinct tasks that have inputs
        if (notEmpty(this.tasks)) {
            for (const task of this.tasks) {
                const key = task.C_WorkflowTask_key;
                if (seen[key]) {
                    // Already added
                    continue;
                }

                if (!task.TaskInput || (task.TaskInput.length === 0)) {
                    // No inputs
                    continue;
                }

                // This one is a keeper
                tasks.push(task);
                seen[key] = true;
            }
        }

        // Use these tasks
        this.bulkInputTasks = tasks;

        // If there is only one task, select it by default
        if (this.bulkInputTasks.length === 1) {
            this.bulkInputTask = this.bulkInputTasks[0];

            // Pretend like the user picked this task to update the related
            // values
            this.bulkInputTaskChanged();
        }
    }

    /**
     * Clear the Bulk Input fill down form.
     *
     * @param clearTask If true, the selected task will also be cleared
     */
    resetBulkInput(clearTask = true) {
        if (clearTask) {
            this.bulkInputTask = null;
        }

        this.bulkInputTaskAliases = [];
        this.bulkInputSelectedTaskAliases = [];
        this.bulkInputCohorts = [];
        this.bulkInputCohortKeys = [];
        this.bulkInputInputs = [];
        this.bulkInputInput = null;
        this.bulkInputValue = null;
    }

    /**
     * The selection of the Bulk Input Task has changed
     */
    bulkInputTaskChanged() {
        // Clear the form, but keep the task selection
        this.resetBulkInput(false);

        if (!this.tasks || !this.bulkInputTask) {
            // Nothing to do
            return;
        }

        // Find all the distinct aliases for this task.
        const aliases: string[] = [];
        const seen: any = {};
        const cohorts: any[] = [];
        const seenCohorts: any = {};
        for (const task of this.tasks) {
            if (task.C_WorkflowTask_key !== this.bulkInputTask.C_WorkflowTask_key) {
                // Not the selected task
                continue;
            }

            if (!task.TaskInput || (task.TaskInput.length === 0)) {
                // This task has no inputs
                continue;
            }
            if (task.IsLocked) {
                // Not the selected task
                continue;
            }

            const alias = task.TaskAlias;
            if (seen[alias]) {
                // Already added
            } else {
                // This one is a keeper
                aliases.push(alias);
                seen[alias] = true;
            }

            for (const taskCohort of task.TaskCohort) {
                const cohort = taskCohort.Cohort;
                if (seenCohorts[cohort.C_Cohort_key]) {
                    // Already added
                    continue;
                }

                // This one is a keeper
                cohorts.push(cohort);
                seenCohorts[cohort.C_Cohort_key] = true;
            }
        }

        if ((aliases.length === 1) && (aliases[0] === this.bulkInputTask.WorkflowTask.TaskName)) {
            // There are no aliases that are different the task name, so clear
            // the list.
            aliases.length = 0;
        }

        // Use these aliases
        this.bulkInputTaskAliases = aliases;
        this.bulkInputCohorts = cohorts;

        // Copy the task inputs into the form
        if (this.bulkInputTask.TaskInput) {
            const inputs = this.bulkInputTask.TaskInput.map((taskInput: any) => {
                return taskInput.Input;
            });

            // Put the inputs in the right order
            inputs.sort((a: any, b: any) => a.SortOrder - b.SortOrder);

            // Use the inputs
            this.bulkInputInputs = inputs;

            // Select the first one by default
            this.bulkInputInput = this.bulkInputInputs[0];
        }
    }

    /**
     * The Bulk Input Input selection was changed
     */
    bulkInputInputChanged() {
        // Clear the prior value
        this.bulkInputValue = null;
    }

    /**
     * The Bulk Input "Update All" button was clicked.
     *
     * Update the inputs for all the matching tasks.
     */
    async bulkInputUpdated(): Promise<void> {
        if (!this.tasks || !this.bulkInputTask || !this.bulkInputInput) {
            // Nothing to do
            return;
        }

        try {
            this.setLoading(true);

            await this.jobPharmaTasksInputsTableService.bulkUpdateInput({
                jobKey: this.job.C_Job_key,
                workflowTaskKey: this.bulkInputTask.C_WorkflowTask_key,
                taskNames: this.bulkInputSelectedTaskAliases,
                cohortKeys: this.bulkInputCohortKeys,
                inputKey: this.bulkInputInput.C_Input_key,
                value: this.bulkInputValue,
            });
            this.loggingService.logFacetSaveSuccess(this.COMPONENT_LOG_TAG, true)?.attr("data-automation-id", formatDataAutomationId("changes-saved", "", "-text"));
        } catch(err) {
            this.setLoading(false);

            throw err;
        } finally {
            this.setLoading(false);

            // Clear the Bulk Input form
            this.resetBulkInput(true);
        }
    }

    async changeTaskPage(newPage: number): Promise<void> {
        try {
            this.setLoading(true);

            this.taskPage = newPage;
            await this.loadMemberTaskInstancesWithExpands(this.taskPage);
        }  catch (error) {
            this.loggingService.logError(ERROR_LOADING, error, this.COMPONENT_LOG_TAG, true);
        } finally {
            this.setLoading(false);
        }
    }

    isRegularInput(input: any): boolean {
        return !input || !input.cv_DataType ||
            (input.cv_DataType.DataType !== DataType.DOSING_TABLE &&
                input.cv_DataType.DataType !== DataType.JOB_CHARACTERISTIC);
    }

    isDosingTableInput(input: any): boolean {
        return input && input.cv_DataType && input.cv_DataType.DataType === DataType.DOSING_TABLE;
    }

    isJobCharacteristicInput(input: any): boolean {
        return input && input.cv_DataType && input.cv_DataType.DataType === DataType.JOB_CHARACTERISTIC;
    }
}
